import GaugeChart from "react-gauge-chart";
import { Card, Grid, Header, Segment, Statistic } from "semantic-ui-react";
import { LicenceType } from "../models/globalTypes";
import { Licence } from "../models/Licence";
import LicenceExpiryTimeline from "./LicenceExpiryTimeline";

interface Props {
  licences?: Licence[] | null;
  isLoading: boolean;
}

const getGaugePercent = (used: number, capacity: number) => {
  if (capacity <= 0) {
    return 0;
  }

  return Math.min(1, Math.max(0, used / capacity));
};

const LicencesDashboard = ({ licences, isLoading }: Props) => {
  const viewerLicences = licences?.filter((l) => l.type === LicenceType.VIEWER) ?? [];
  const standardLicences = licences?.filter((l) => l.type === LicenceType.STANDARD) ?? [];
  const activeViewerLicences = viewerLicences.filter((l) => l.isActive);
  const activeStandardLicences = standardLicences.filter((l) => l.isActive);

  const viewerLicensedUsers = activeViewerLicences.reduce((sum, l) => sum + l.numberOfUsers, 0);
  const standardLicensedUsers = activeStandardLicences.reduce((sum, l) => sum + l.numberOfUsers, 0);

  const viewerAvailableSlots = viewerLicences.reduce((sum, l) => sum + l.availableUserSlots!, 0);
  const standardAvailableSlots = standardLicences.reduce((sum, l) => sum + l.availableUserSlots!, 0);

  const viewerUsedSlots = viewerLicensedUsers - viewerAvailableSlots;
  const standardUsedSlots = standardLicensedUsers - standardAvailableSlots;

  return (
    <Segment basic loading={isLoading}>
      <Grid stackable columns={2}>
        <Grid.Column>
          <Card fluid>
            <Card.Content>
              <Header as="h5" textAlign="center">
                Standard
              </Header>
              <Grid columns={2} verticalAlign="middle">
                <Grid.Column width={8}>
                  <GaugeChart
                    id="standard-licence-capacity-gauge"
                    nrOfLevels={100}
                    arcsLength={[0.75, 0.2, 0.05]}
                    colors={["#21ba45", "#fbbd08", "#db2828"]}
                    percent={getGaugePercent(standardUsedSlots, standardLicensedUsers)}
                    arcPadding={0.02}
                    textColor="#555"
                    formatTextValue={() => `${standardUsedSlots}/${standardLicensedUsers}`}
                  />
                </Grid.Column>
                <Grid.Column width={8} textAlign="center">
                  <Statistic size="mini" color="blue" style={{ marginBottom: "0.75rem" }}>
                    <Statistic.Label>Total</Statistic.Label>
                    <Statistic.Value>{standardLicensedUsers}</Statistic.Value>
                  </Statistic>
                  <Statistic size="mini" color={standardAvailableSlots < 0 ? "red" : "green"}>
                    <Statistic.Label>Available</Statistic.Label>
                    <Statistic.Value>{standardAvailableSlots}</Statistic.Value>
                  </Statistic>
                </Grid.Column>
              </Grid>
            </Card.Content>
          </Card>
        </Grid.Column>
        <Grid.Column>
          <Card fluid>
            <Card.Content>
              <Header as="h5" textAlign="center">
                Viewer
              </Header>
              <Grid columns={2} verticalAlign="middle">
                <Grid.Column width={8}>
                  <GaugeChart
                    id="viewer-licence-capacity-gauge"
                    nrOfLevels={100}
                    arcsLength={[0.75, 0.2, 0.05]}
                    colors={["#21ba45", "#fbbd08", "#db2828"]}
                    percent={getGaugePercent(viewerUsedSlots, viewerLicensedUsers)}
                    arcPadding={0.02}
                    textColor="#555"
                    formatTextValue={() => `${viewerUsedSlots}/${viewerLicensedUsers}`}
                  />
                </Grid.Column>
                <Grid.Column width={8} textAlign="center">
                  <Statistic size="mini" color="blue" style={{ marginBottom: "0.75rem" }}>
                    <Statistic.Label>Total</Statistic.Label>
                    <Statistic.Value>{viewerLicensedUsers}</Statistic.Value>
                  </Statistic>
                  <Statistic size="mini" color={viewerAvailableSlots < 0 ? "red" : "green"}>
                    <Statistic.Label>Available</Statistic.Label>
                    <Statistic.Value>{viewerAvailableSlots}</Statistic.Value>
                  </Statistic>
                </Grid.Column>
              </Grid>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>
      <Grid stackable columns={2} style={{ marginTop: '0.5rem' }}>
        <Grid.Column>
          <LicenceExpiryTimeline licences={licences} type={LicenceType.STANDARD} label="Standard" />
        </Grid.Column>
        <Grid.Column>
          <LicenceExpiryTimeline licences={licences} type={LicenceType.VIEWER} label="Viewer" />
        </Grid.Column>
      </Grid>
    </Segment>
  );
};

export default LicencesDashboard;
